import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyDisputes, fileDispute, respondToDispute, getDisputeReasons, getRentals } from '../services/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { AlertTriangle, Shield, Clock, CheckCircle, XCircle, FileText, Plus } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  open: 'bg-[#F59E0B]/10 text-[#F59E0B]',
  under_review: 'bg-[#3B82F6]/10 text-[#3B82F6]',
  resolved: 'bg-[#4CAF50]/10 text-[#4CAF50]',
  dismissed: 'bg-[#596652]/10 text-[#596652]',
};

const statusIcons = {
  open: AlertTriangle,
  under_review: Clock,
  resolved: CheckCircle,
  dismissed: XCircle,
};

const DisputesPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [disputes, setDisputes] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [reasons, setReasons] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [showRespondDialog, setShowRespondDialog] = useState(null);

  // File dispute form
  const [selectedRental, setSelectedRental] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [description, setDescription] = useState('');
  const [filing, setFiling] = useState(false);

  // Respond form
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchData();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [d, r, reasons] = await Promise.all([
        getMyDisputes(),
        getRentals('renter').catch(() => []),
        getDisputeReasons()
      ]);
      setDisputes(d);
      setRentals(r);
      setReasons(reasons);
    } catch (err) {
      console.error('Failed to fetch disputes:', err);
      toast.error('Failed to load disputes');
    } finally { setLoading(false); }
  };

  const handleFileDispute = async () => {
    if (!selectedRental || !selectedReason || !description.trim()) {
      toast.error('Please fill all fields');
      return;
    }
    setFiling(true);
    try {
      await fileDispute({
        rental_id: selectedRental,
        reason: selectedReason,
        description: description.trim(),
        evidence_urls: []
      });
      toast.success('Dispute filed successfully');
      setShowFileDialog(false);
      setSelectedRental('');
      setSelectedReason('');
      setDescription('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to file dispute');
    } finally { setFiling(false); }
  };

  const handleRespond = async (disputeId) => {
    if (!responseText.trim()) { toast.error('Please enter your response'); return; }
    setResponding(true);
    try {
      await respondToDispute(disputeId, responseText.trim());
      toast.success('Response submitted');
      setShowRespondDialog(null);
      setResponseText('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setResponding(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"><div className="animate-pulse text-[#596652]">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-[#2C3329] mb-1">Disputes</h1>
            <p className="text-[#596652]">Resolve issues with rentals</p>
          </div>
          <Dialog open={showFileDialog} onOpenChange={setShowFileDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#D97757] text-white rounded-full hover:bg-[#C56547]" data-testid="file-dispute-button">
                <Plus className="w-4 h-4 mr-2" />File Dispute
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-xl">File a Dispute</DialogTitle><DialogDescription className="sr-only">Describe the issue with your rental</DialogDescription></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-[#2C3329] mb-2">Select Rental</label>
                  <Select value={selectedRental} onValueChange={setSelectedRental}>
                    <SelectTrigger className="w-full border-[#EBE8DF] rounded-xl" data-testid="dispute-rental-select">
                      <SelectValue placeholder="Choose a rental" />
                    </SelectTrigger>
                    <SelectContent>
                      {rentals.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.item_title} ({r.status})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C3329] mb-2">Reason</label>
                  <Select value={selectedReason} onValueChange={setSelectedReason}>
                    <SelectTrigger className="w-full border-[#EBE8DF] rounded-xl" data-testid="dispute-reason-select">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(reasons).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C3329] mb-2">Description</label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe the issue in detail..."
                    className="border-[#EBE8DF] rounded-xl"
                    rows={4}
                    data-testid="dispute-description"
                  />
                </div>
                <Button
                  onClick={handleFileDispute}
                  disabled={filing || !selectedRental || !selectedReason || !description.trim()}
                  className="w-full bg-[#D97757] text-white rounded-full hover:bg-[#C56547]"
                  data-testid="submit-dispute-button"
                >
                  {filing ? 'Filing...' : 'Submit Dispute'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Disputes List */}
        {disputes.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#EBE8DF]">
            <Shield className="w-12 h-12 text-[#EBE8DF] mx-auto mb-4" />
            <p className="text-[#596652] mb-2">No disputes</p>
            <p className="text-[#596652]/60 text-sm">All your rentals are going smoothly!</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="disputes-list">
            {disputes.map((dispute) => {
              const StatusIcon = statusIcons[dispute.status] || AlertTriangle;
              const isFiler = dispute.filed_by === user?.id;

              return (
                <div key={dispute.id} className="bg-white rounded-2xl border border-[#EBE8DF] p-6" data-testid={`dispute-${dispute.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <StatusIcon className={`w-5 h-5 ${dispute.status === 'open' ? 'text-[#F59E0B]' : dispute.status === 'resolved' ? 'text-[#4CAF50]' : 'text-[#596652]'}`} />
                      <div>
                        <h3 className="font-heading font-medium text-[#2C3329]">{dispute.rental_title || 'Rental'}</h3>
                        <p className="text-xs text-[#596652]">
                          {isFiler ? `Filed against ${dispute.against_user_name}` : `Filed by ${dispute.filed_by_name}`}
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[dispute.status]}`}>
                      {dispute.status.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="mb-3">
                    <span className="text-xs font-medium text-[#8C9A76] uppercase">{reasons[dispute.reason] || dispute.reason}</span>
                    <p className="text-sm text-[#596652] mt-1">{dispute.description}</p>
                  </div>

                  {/* Resolution info */}
                  {dispute.resolution && (
                    <div className="bg-[#FDFBF7] rounded-xl p-3 mb-3">
                      <p className="text-sm font-medium text-[#2C3329]">Resolution: {dispute.resolution}</p>
                      {dispute.payout_amount > 0 && (
                        <p className="text-sm text-[#4CAF50] mt-1">Payout: ${dispute.payout_amount.toFixed(2)}</p>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  {dispute.status === 'open' && !isFiler && (
                    <Dialog open={showRespondDialog === dispute.id} onOpenChange={(open) => setShowRespondDialog(open ? dispute.id : null)}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" className="border-[#EBE8DF] rounded-full" data-testid={`respond-dispute-${dispute.id}`}>
                          <FileText className="w-3 h-3 mr-1" />Respond
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Respond to Dispute</DialogTitle><DialogDescription className="sr-only">Add your response to this dispute</DialogDescription></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <Textarea
                            value={responseText}
                            onChange={(e) => setResponseText(e.target.value)}
                            placeholder="Explain your side..."
                            rows={4}
                            className="border-[#EBE8DF] rounded-xl"
                            data-testid="dispute-response-input"
                          />
                          <Button
                            onClick={() => handleRespond(dispute.id)}
                            disabled={responding}
                            className="w-full bg-[#8C9A76] text-white rounded-full hover:bg-[#7A8A64]"
                            data-testid="submit-response-button"
                          >
                            {responding ? 'Submitting...' : 'Submit Response'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}

                  <p className="text-[10px] text-[#8C9A76] mt-2">
                    Filed {new Date(dispute.created_at).toLocaleDateString()}
                    {dispute.resolved_at && ` • Resolved ${new Date(dispute.resolved_at).toLocaleDateString()}`}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DisputesPage;
